import { createSlice } from "@reduxjs/toolkit";
import { biReportLinksV2 } from "../../../sharedfiles/EndpointConfig";
import { apiCaller } from "../../../api/apiCaller";
import { getListOfURLs, toaster, downloadZipFile } from "../../../sharedfiles/helper";

export const bireportSlice = createSlice({
    name: "bireportSlice",
    initialState: {
        biReportData: [],
        biReportDataNew: {},
        dynamicFilterOptions: {},
        totalCount: {},
        pagination: {},
        vipBiReportData: [],
        isVisible: false,
        sportsData: [],
        biReportURLs: {},
        downloadAllLoader: {},
        downloadTimestamps: {}, // Track when each download was initiated
    },

    reducers: {
        setBiReportData: (state, action) => {
            state.biReportData = action.payload;
        },
        setBiReportDataNew: (state, action) => {
            const { reportName, data } = action.payload;
            state.biReportDataNew[reportName] = data;
        },
        setDynamicFilterOptions: (state, action) => {
            const { reportName, filters } = action.payload;
            state.dynamicFilterOptions[reportName] = filters;
        },
        setTotalCountForReport: (state, action) => {
            const { reportName, totalCount } = action.payload;
            state.totalCount[reportName] = totalCount;
        },
        setPaginationForReport: (state, action) => {
            const { reportName, firstValue, secondValue, recordsShow, isVisible } = action.payload;

            state.pagination[reportName] = {
                firstValue,
                secondValue,
                recordsShow,
                isVisible
            };
        },
        setVipBiReportData: (state, action) => {
            state.vipBiReportData = action.payload;
        },
        setPaginationFirstValue: (state, action) => {
            state.paginationFirstValue = action.payload;
        },
        setPaginationSecondValue: (state, action) => {
            state.paginationSecondValue = action.payload;
        },
        setRecordsShow: (state, action) => {
            state.recordsShow = action.payload;
        },
        setIsVisible: (state, action) => {
            state.isVisible = action.payload;
        },
        setSportsData: (state, action) => {
            state.sportsData = action.payload;
        },
        setBiReportURLs: (state, action) => {
            state.biReportURLs = action.payload;
        },
        setDownloadAllLoader: (state, action) => {
            // If reportName is null/undefined, clear all loaders (reset to empty object)
            if (!action.payload.reportName && action.payload.reportName !== 0) {
                state.downloadAllLoader = {};
                state.downloadTimestamps = {};
            } else {
                const { reportName, loading } = action.payload;
                state.downloadAllLoader[reportName] = loading;
                // Track timestamp when download starts
                if (loading) {
                    state.downloadTimestamps[reportName] = Date.now();
                    // Save to localStorage
                    const activeDownloads = JSON.parse(localStorage.getItem('bi_active_downloads') || '[]');
                    if (!activeDownloads.includes(reportName)) {
                        activeDownloads.push(reportName);
                        localStorage.setItem('bi_active_downloads', JSON.stringify(activeDownloads));
                    }
                } else {
                    // Clear timestamp when download completes
                    delete state.downloadTimestamps[reportName];
                    // Remove from localStorage
                    const activeDownloads = JSON.parse(localStorage.getItem('bi_active_downloads') || '[]');
                    const updatedDownloads = activeDownloads.filter(id => id !== reportName);
                    localStorage.setItem('bi_active_downloads', JSON.stringify(updatedDownloads));
                }
            }
        },
        initializeDownloadersFromLocalStorage: (state) => {
            const activeDownloads = JSON.parse(localStorage.getItem('bi_active_downloads') || '[]');
            activeDownloads.forEach(reportName => {
                state.downloadAllLoader[reportName] = true;
                state.downloadTimestamps[reportName] = Date.now();
            });
        }
    }
});

export const {
    setBiReportData,
    setVipBiReportData,
    setPaginationFirstValue,
    setPaginationSecondValue,
    setRecordsShow,
    setIsVisible,
    setBiReportDataNew,
    setPaginationForReport,
    setTotalCountForReport,
    setDynamicFilterOptions,
    setSportsData,
    setBiReportURLs,
    setReportsLoader,
    setDownloadAllLoader,
    initializeDownloadersFromLocalStorage
} = bireportSlice.actions;

export const downloadAllRecord = (pageNumber, itemsPerPage, payload) => async (dispatch) => {
    const url = biReportLinksV2.downloadAllReports(pageNumber, itemsPerPage);
    const reportName = payload?.reportId;

    // Set loading to true immediately for this specific report
    dispatch(setDownloadAllLoader({ reportName, loading: true }));

    try {
        const response = await apiCaller(url, "POST", payload);
        if (response.status === 202 || response.data?.status === 202 || response.status === 200 || response.data?.status === 200) {
            return response.data;
        }

        dispatch(setDownloadAllLoader({ reportName, loading: false }));
        return response.data;
    } catch (err) {
        // Clear loading state on error
        dispatch(setDownloadAllLoader({ reportName, loading: false }));

        const errorMsg = err?.response?.data?.message || err?.message || err?.msg || "Download failed. Please try again.";
        toaster.error(errorMsg.replaceAll("_", " "));
        return null;
    }
};


export const getListOfURLsCaller = () => (dispatch) => {
    dispatch(getListOfURLs("ALL_DOWNLOAD_URLS"));
}

export const getBiReportData =
    (pageNumber, itemsPerPage, payload, reportData, reportName) =>
        async (dispatch) => {
            const url = biReportLinksV2.sportsBettingHistoryReport(pageNumber, itemsPerPage);

            try {
                const response = await apiCaller(url, "POST", payload);
                const rows = response.data?.rows || [];
                const totalCount = response.data?.totalCount || 0;

                const dynamicFilters = {
                    customer_id: [
                        ...new Set(rows.map(r => r.customer_id).filter(x => x && x !== 0))
                    ],
                    bonus_tags: [
                        ...new Set(
                            rows
                                .map(r => r.bonus_tags)
                                .map(x => x ? JSON.parse(x.replace(/'/g, '"')) : [])
                                .flat()
                                .filter(x => x)
                        )
                    ],
                    bonus_code: [
                        ...new Set(rows.map(r => r.bonus_code).filter(x => x))
                    ],
                    btag: [
                        ...new Set(rows.map(r => r.btag).filter(x => x))
                    ],
                    affiliate_btag: [
                        ...new Set(rows.map(r => r.affiliate_btag).filter(x => x))
                    ],
                    affliate_btag: [
                        ...new Set(rows.map(r => r.affliate_btag).filter(x => x))
                    ],
                    affiliate_name: [
                        ...new Set(rows.map(r => r.affiliate_name).filter(x => x))
                    ],
                    user_name: [
                        ...new Set(rows.map(r => r.user_name).filter(x => x))
                    ],
                    username: [
                        ...new Set(rows.map(r => r.username).filter(x => x))
                    ],
                    state: [
                        ...new Set(rows.map(r => r.state).filter(x => x))
                    ],
                    channel: [
                        ...new Set(rows.map(r => r.channel).filter(x => x))
                    ],
                    systemtag: [
                        ...new Set(rows.map(r => r.systemtag).filter(x => x))
                    ],
                };
                dispatch(setDynamicFilterOptions({ reportName, filters: dynamicFilters }));

                const safeData = rows.length === 0
                    ? [reportData.tableHeaders, reportData.tableHeaders]
                    : [
                        reportData.tableHeaders,
                        reportData.tableHeaders,
                        ...rows.map((row) =>
                            reportData.tableData.map((key) => {
                                const val = row[key];
                                const isIdField = key === 'customer_id' || key.endsWith('_id');
                                if (!isIdField && typeof val === 'number' && !isNaN(val)) {
                                    return val.toLocaleString('en-US', { maximumFractionDigits: 2 });
                                }
                                return val;
                            })
                        ),
                    ];

                dispatch(setBiReportDataNew({ reportName, data: safeData }));
                dispatch(setTotalCountForReport({ reportName, totalCount }));
                dispatch(
                    setPaginationForReport({
                        reportName,
                        firstValue: pageNumber + 1,
                        secondValue: itemsPerPage,
                        recordsShow: [pageNumber + 1, itemsPerPage],
                        isVisible: true,
                    })
                );
            } catch (err) {
                dispatch(
                    setBiReportDataNew({
                        reportName,
                        data: [reportData.tableHeaders, reportData.tableHeaders],
                    })
                );
                dispatch(
                    setPaginationForReport({
                        reportName,
                        firstValue: pageNumber + 1,
                        secondValue: itemsPerPage,
                        recordsShow: [pageNumber + 1, itemsPerPage],
                        isVisible: false,
                    })
                );
            }
        };

export const getSportsData = (payload) => async (dispatch) => {
    const url = biReportLinksV2.sportsApi;
    try {
        const query = new URLSearchParams(payload).toString();
        const response = await apiCaller(`${url}?${query}`, "GET");
        dispatch(setSportsData(response.data || []));
        return response.data || [];
    } catch (err) {
        return [];
    }
};


export default bireportSlice.reducer;
